import { normalizeClaudeProviderEvents } from "../providerEvents/claude"
import { type CodexEvent, normalizeCodexProviderEvents } from "../providerEvents/codex"
import { calculateCodexCostUsd } from "../providerEvents/codexPricing"
import { xaiCostUsdFromTicks } from "../providerEvents/grokUsage"
import type { HarnessStreamEvent, HarnessUsage } from "../providerEvents/harnessEventTypes"
import { extractRawMessageEvents } from "../providerEvents/harnessEventTypes"
import type { ActionEvent, CodeEvent, HarnessId } from "../types"

export const TASK_USAGE_STATS_VERSION = 3

export interface TaskUsageStats {
    usageVersion: number
    inputTokens: number
    outputTokens: number
    totalCostUsd: number
    eventCount: number
    costByModel: Record<string, number>
    durationMs: number
}

interface CodexUsageSnapshot {
    inputTokens: number
    outputTokens: number
    cacheReadTokens: number
}

interface GrokUsageSnapshot {
    inputTokens: number
    outputTokens: number
    durationMs: number
    costUsd: number
}

interface OpenCodeUsageSnapshot {
    inputTokens: number
    outputTokens: number
    durationMs: number
    costUsd: number
}

interface UsageEntry {
    harnessId: HarnessId
    modelId: string
    fastMode?: boolean
    events: HarnessStreamEvent[]
    sessionId?: string
    parentSessionId?: string
}

export function needsTaskUsageBackfill(usage?: TaskUsageStats): boolean {
    return !usage || usage.durationMs === undefined || usage.usageVersion !== TASK_USAGE_STATS_VERSION
}

export function normalizeTaskUsage(usage?: Partial<TaskUsageStats>): TaskUsageStats {
    return {
        usageVersion: TASK_USAGE_STATS_VERSION,
        inputTokens: usage?.inputTokens ?? 0,
        outputTokens: usage?.outputTokens ?? 0,
        totalCostUsd: usage?.totalCostUsd ?? 0,
        eventCount: usage?.eventCount ?? 0,
        costByModel: usage?.costByModel ?? {},
        durationMs: usage?.durationMs ?? 0,
    }
}

export function computeTaskUsage(events: Array<CodeEvent & { id: string }>): TaskUsageStats {
    let inputTokens = 0
    let outputTokens = 0
    let totalCostUsd = 0
    let eventCount = 0
    let durationMs = 0
    const costByModel: Record<string, number> = {}
    const codexSessionTotals = new Map<string, CodexUsageSnapshot>()
    const openCodeSessionTotals = new Map<string, OpenCodeUsageSnapshot>()

    for (const event of events) {
        if (event.type !== "action") continue
        eventCount++

        for (const entry of getUsageEntries(event)) {
            const messageEvents = extractRawMessageEvents(entry.events)
            if (entry.harnessId === "grok-build") {
                const providerUsage = extractGrokUsage(messageEvents.filter((event) => event.harnessId === "grok-build").map((event) => event.message))
                const completeUsage = extractCompleteUsage(entry.events)
                const usage = providerUsage ?? completeUsage
                if (usage) {
                    inputTokens += usage.inputTokens
                    outputTokens += usage.outputTokens
                    durationMs += usage.durationMs ?? 0
                    const cost = usage.costUsd ?? 0
                    totalCostUsd += cost
                    costByModel[entry.modelId] = (costByModel[entry.modelId] ?? 0) + cost
                }
                continue
            }
            if (entry.harnessId === "claude-code") {
                const claudeEvents = normalizeClaudeProviderEvents(messageEvents.filter((evt) => evt.harnessId === "claude-code").map((evt) => evt.message))
                for (const evt of claudeEvents) {
                    if (evt.type === "result") {
                        const resultCostUsd = evt.total_cost_usd ?? 0
                        totalCostUsd += resultCostUsd
                        const usage = evt.usage as { input_tokens?: number; output_tokens?: number } | undefined
                        inputTokens += usage?.input_tokens ?? 0
                        outputTokens += usage?.output_tokens ?? 0
                        costByModel[entry.modelId] = (costByModel[entry.modelId] ?? 0) + resultCostUsd
                        durationMs += evt.duration_ms ?? 0
                    }
                }
                continue
            }

            if (entry.harnessId === "opencode") {
                const completeUsage = extractCompleteUsage(entry.events)
                const providerUsage = extractOpenCodeUsage(messageEvents.filter((event) => event.harnessId === "opencode").map((event) => event.message))
                let usage = providerUsage
                if (providerUsage && completeUsage && entry.sessionId) {
                    openCodeSessionTotals.set(entry.sessionId, openCodeUsageFromHarnessUsage(completeUsage))
                } else if (!providerUsage && completeUsage) {
                    usage = normalizeOpenCodeCumulativeUsage({
                        snapshot: openCodeUsageFromHarnessUsage(completeUsage),
                        sessionId: entry.sessionId,
                        parentSessionId: entry.parentSessionId,
                        sessionTotals: openCodeSessionTotals,
                    })
                }
                if (usage) {
                    inputTokens += usage.inputTokens
                    outputTokens += usage.outputTokens
                    durationMs += usage.durationMs
                    const cost = usage.costUsd
                    totalCostUsd += cost
                    costByModel[entry.modelId] = (costByModel[entry.modelId] ?? 0) + cost
                }
                continue
            }

            const codexEvents = normalizeCodexProviderEvents(messageEvents.filter((evt) => evt.harnessId === "codex").map((evt) => evt.message)).events
            const codexUsage = extractCodexUsageSnapshot(codexEvents, entry.events)
            if (codexUsage) {
                const sessionId = entry.sessionId ?? extractCodexSessionId(codexEvents)
                const usageToAdd = normalizeCodexUsage({
                    snapshot: codexUsage,
                    sessionId,
                    parentSessionId: entry.parentSessionId,
                    sessionTotals: codexSessionTotals,
                })

                inputTokens += usageToAdd.inputTokens
                outputTokens += usageToAdd.outputTokens

                const computedCostUsd = calculateCodexCostUsd(entry.modelId, usageToAdd.inputTokens, usageToAdd.outputTokens, usageToAdd.cacheReadTokens, {
                    fastMode: entry.fastMode,
                })
                if (computedCostUsd !== undefined) {
                    totalCostUsd += computedCostUsd
                    costByModel[entry.modelId] = (costByModel[entry.modelId] ?? 0) + computedCostUsd
                }
            }

            const completeUsage = extractCompleteUsage(entry.events)
            if (completeUsage?.durationMs) {
                durationMs += completeUsage.durationMs
            }
        }
    }

    return {
        usageVersion: TASK_USAGE_STATS_VERSION,
        inputTokens,
        outputTokens,
        totalCostUsd,
        eventCount,
        costByModel,
        durationMs,
    }
}

function extractOpenCodeUsage(messages: unknown[]): OpenCodeUsageSnapshot | undefined {
    const completed = new Map<string, OpenCodeUsageSnapshot>()
    for (const message of messages) {
        const raw = recordValue(message)
        if (typeof raw?.type !== "string" || raw.type.replace(/\.1$/, "") !== "message.updated") continue
        const properties = recordValue(raw.properties) ?? raw
        const info = recordValue(properties.info)
        if (!info || info.role !== "assistant") continue
        const id = typeof info.id === "string" ? info.id : undefined
        const timing = recordValue(info.time)
        const createdAt = numberValue(timing?.created)
        const completedAt = numberValue(timing?.completed)
        if (!id || completedAt === undefined) continue
        const tokens = recordValue(info.tokens)
        completed.set(id, {
            inputTokens: numberValue(tokens?.input) ?? 0,
            outputTokens: numberValue(tokens?.output) ?? 0,
            durationMs: createdAt === undefined ? 0 : Math.max(0, completedAt - createdAt),
            costUsd: numberValue(info.cost) ?? 0,
        })
    }
    if (completed.size === 0) return undefined
    const total: OpenCodeUsageSnapshot = { inputTokens: 0, outputTokens: 0, durationMs: 0, costUsd: 0 }
    for (const usage of completed.values()) {
        total.inputTokens += usage.inputTokens
        total.outputTokens += usage.outputTokens
        total.durationMs += usage.durationMs
        total.costUsd += usage.costUsd
    }
    return total
}

function openCodeUsageFromHarnessUsage(usage: HarnessUsage): OpenCodeUsageSnapshot {
    return {
        inputTokens: usage.inputTokens,
        outputTokens: usage.outputTokens,
        durationMs: usage.durationMs ?? 0,
        costUsd: usage.costUsd ?? 0,
    }
}

function normalizeOpenCodeCumulativeUsage({
    snapshot,
    sessionId,
    parentSessionId,
    sessionTotals,
}: {
    snapshot: OpenCodeUsageSnapshot
    sessionId?: string
    parentSessionId?: string
    sessionTotals: Map<string, OpenCodeUsageSnapshot>
}): OpenCodeUsageSnapshot {
    if (!sessionId) return snapshot
    const previous = sessionTotals.get(sessionId)
    sessionTotals.set(sessionId, snapshot)
    if (!previous || parentSessionId !== sessionId || snapshot.inputTokens < previous.inputTokens || snapshot.outputTokens < previous.outputTokens) {
        return snapshot
    }
    return {
        inputTokens: snapshot.inputTokens - previous.inputTokens,
        outputTokens: snapshot.outputTokens - previous.outputTokens,
        durationMs: Math.max(0, snapshot.durationMs - previous.durationMs),
        costUsd: Math.max(0, snapshot.costUsd - previous.costUsd),
    }
}

function extractGrokUsage(messages: unknown[]): GrokUsageSnapshot | undefined {
    let total: GrokUsageSnapshot | undefined
    for (const message of messages) {
        const raw = recordValue(message)
        const params = recordValue(raw?.params)
        const update = recordValue(params?.update)
        if (update?.sessionUpdate !== "turn_completed") continue
        const usage = recordValue(update.usage)
        if (!usage) continue
        total ??= { inputTokens: 0, outputTokens: 0, durationMs: 0, costUsd: 0 }
        total.inputTokens += numberValue(usage.inputTokens) ?? 0
        total.outputTokens += numberValue(usage.outputTokens) ?? 0
        total.durationMs += numberValue(usage.apiDurationMs) ?? 0
        total.costUsd += xaiCostUsdFromTicks(numberValue(usage.costUsdTicks))
    }
    return total
}

function recordValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function numberValue(value: unknown): number | undefined {
    return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

/**
 * Format a duration in ms as a compact human-readable string.
 *
 *   < 1s     → "0.0s" (1 decimal)
 *   < 1min   → "12.3s" (1 decimal)
 *   < 1hr    → "5m 23s"
 *   < 1day   → "2h 14m"
 *   ≥ 1day   → "1.5h" (decimal hours)
 */
export function formatDuration(durationMs: number): string {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return "0s"
    const totalSeconds = durationMs / 1000
    if (totalSeconds < 60) return `${totalSeconds.toFixed(1)}s`
    const totalMinutes = totalSeconds / 60
    if (totalMinutes < 60) {
        const m = Math.floor(totalMinutes)
        const s = Math.round(totalSeconds - m * 60)
        return s === 0 ? `${m}m` : `${m}m ${s}s`
    }
    const totalHours = totalMinutes / 60
    if (totalHours < 24) {
        const h = Math.floor(totalHours)
        const m = Math.round(totalMinutes - h * 60)
        return m === 0 ? `${h}h` : `${h}h ${m}m`
    }
    return `${totalHours.toFixed(1)}h`
}

export function formatTaskCost(totalCostUsd: number): string {
    if (totalCostUsd <= 0) return "$0.00"
    if (totalCostUsd < 0.0001) return "<$0.0001"
    return totalCostUsd < 0.01 ? `$${totalCostUsd.toFixed(4)}` : `$${totalCostUsd.toFixed(2)}`
}

function getUsageEntries(event: ActionEvent & { id: string }): UsageEntry[] {
    const mainModelId = event.execution.modelId ?? "unknown"
    return [
        {
            harnessId: event.execution.harnessId ?? "claude-code",
            modelId: mainModelId,
            fastMode: event.execution.fastMode,
            events: event.execution.events,
            sessionId: event.execution.sessionId,
            parentSessionId: event.execution.parentSessionId,
        },
    ]
}

function extractCompleteUsage(events: HarnessStreamEvent[]): HarnessUsage | undefined {
    for (const e of events) {
        if (e.direction === "execution" && e.type === "complete" && e.usage) {
            return e.usage as HarnessUsage
        }
    }
    return undefined
}

function extractCodexUsageSnapshot(messageEvents: CodexEvent[], streamEvents: HarnessStreamEvent[]): CodexUsageSnapshot | undefined {
    let latestTurnUsage: { inputTokens: number; outputTokens: number; cacheReadTokens: number } | undefined
    for (const evt of messageEvents) {
        if (evt.type === "turn.completed") {
            latestTurnUsage = {
                inputTokens: evt.usage.input_tokens ?? 0,
                outputTokens: evt.usage.output_tokens ?? 0,
                cacheReadTokens: evt.usage.cached_input_tokens ?? 0,
            }
        }
    }

    const completeUsage = extractCompleteUsage(streamEvents)
    const input = completeUsage?.inputTokens ?? latestTurnUsage?.inputTokens
    const output = completeUsage?.outputTokens ?? latestTurnUsage?.outputTokens
    const cacheRead = completeUsage?.cacheReadTokens ?? latestTurnUsage?.cacheReadTokens ?? 0

    if (input === undefined && output === undefined) return undefined

    return {
        inputTokens: input ?? 0,
        outputTokens: output ?? 0,
        cacheReadTokens: cacheRead,
    }
}

function extractCodexSessionId(messageEvents: CodexEvent[]): string | undefined {
    for (const evt of messageEvents) {
        if (evt.type === "thread.started") {
            return evt.session_id ?? evt.thread_id
        }
    }
    return undefined
}

function normalizeCodexUsage({
    snapshot,
    sessionId,
    parentSessionId,
    sessionTotals,
}: {
    snapshot: CodexUsageSnapshot
    sessionId?: string
    parentSessionId?: string
    sessionTotals: Map<string, CodexUsageSnapshot>
}): CodexUsageSnapshot {
    if (!sessionId) return snapshot

    const prev = sessionTotals.get(sessionId)
    sessionTotals.set(sessionId, snapshot)
    if (!prev) return snapshot

    const canDeltaSameSession = parentSessionId === sessionId
    const looksCumulative = snapshot.inputTokens >= prev.inputTokens && snapshot.outputTokens >= prev.outputTokens

    if (!looksCumulative || !canDeltaSameSession) {
        return snapshot
    }

    return {
        inputTokens: Math.max(0, snapshot.inputTokens - prev.inputTokens),
        outputTokens: Math.max(0, snapshot.outputTokens - prev.outputTokens),
        cacheReadTokens: Math.max(0, snapshot.cacheReadTokens - prev.cacheReadTokens),
    }
}
